import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Xclose } from '../assets/svg/SignInIcons';
import {
  maskEmailID,
  maskPhoneNumber,
  RootState,
  useSecureDeviceId,
  validatePasscodeRequest,
  resetValidatePasscodeSlice,
  blockAccountText,
  signInRequest,
  generateCodeForDeviceRequest,
  encryptAES,
  decryptAES,
  getChannelId,
} from '@rb-serve-app/core';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Spinner from './Spinner';
import InlineError from './ErrorComponents/InlineError';
import { useNavigate } from 'react-router-dom';
import { BalanceIcon } from '../assets/svg/StatusIcon';
import CommanModal from './CommanModal';
import { useRecaptchaToken } from '@rb-serve-app/core';

interface VerificationCodeForm {
  verificationCode: string;
}
interface ModelProps {
  userData: any;
  selectedMethod: string;
  isPreferenceType: string | null;
  setcurrentModelToShow: any;
}

const VerificationCodeModal: React.FC<ModelProps> = ({
  selectedMethod,
  userData,
  isPreferenceType,
  setcurrentModelToShow,
}) => {
  const changeToContactmethod = () => {
    setcurrentModelToShow('VERIFY_IDENTITY');
  };

  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );
  const { entries } = contentfulData;
  const otpContentFullData = entries?.rb_otp_flow;
  const errorContent = otpContentFullData?.blockedUserPopupContent;
  const getRecaptchaToken = useRecaptchaToken();
  const schema = Yup.object().shape({
    verificationCode: Yup.string()
      // .required('Verification code is required.')
      .matches(/^[0-9]+$/, errorContent?.err_num_only)
      .length(8, errorContent?.err_max_length),
  });
  const {
    register,
    handleSubmit,
    watch,
    resetField,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: { verificationCode: '' },
  });

  const selectedOtp = watch('verificationCode') || '';
  const [otp, setOtp] = useState('');
  const [isDisabled, setDisabled] = useState(true);
  const [isValidOtp, setIsValidOtp] = useState(true);
  const [istryAgain, setIstryAgain] = useState(false);
  const [blockUser, setBlockUser] = useState(false);
  const [isSuccessMsg, setIssuccessMsg] = useState(false);
  const [isSuccessLoading, setIsSuccessLoading] = useState(false);
  const [resendClick, setRendClick] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const navigate = useNavigate();

  interface GenerateForDevice {
    statusCode?: string;
    success?: boolean;
    status?: string;
  }

  const {
    isValidatePasscodeLoading,
    isValidatePasscodeError,
    validationPasscode,
  } = useSelector((state: RootState) => state.validatePasscode);

  useEffect(() => {
    setRendClick(false);
    setIssuccessMsg(false);
    setIstryAgain(false);
    setBlockUser(false);
    setOtp(selectedOtp);
    // Don't re-enable button if validation was successful or navigation is in progress
    if (!validationPasscode && !isNavigating) {
      if (selectedOtp?.length === 8 && !errors?.verificationCode?.message) {
        setDisabled(false);
      } else {
        setDisabled(true);
      }
    }
  }, [selectedOtp, errors?.verificationCode, validationPasscode, isNavigating]);

  // Accessibility: Trap focus within the modal
  useEffect(() => {
    const focusableElements = modalRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusableElement = focusableElements?.[0] as HTMLElement;
    const lastFocusableElement = focusableElements?.[
      focusableElements.length - 1
    ] as HTMLElement;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && modalRef.current) {
        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstFocusableElement) {
            event.preventDefault();
            lastFocusableElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastFocusableElement) {
            event.preventDefault();
            firstFocusableElement.focus();
          }
        }
      }
      if (event.key === 'Escape') {
        // Close modal on Esc
        console.log('Modal closed via Escape key');
      }
    };

    modalRef.current?.addEventListener('keydown', handleKeyDown);
    return () =>
      modalRef.current?.removeEventListener('keydown', handleKeyDown);
  }, []);
  console.log('isValidatePasscodeError', isValidatePasscodeError);
  const dispatch = useDispatch();
  const deviceId = useSecureDeviceId() || '';

  const { loading, preferencesUser } = useSelector(
    (state: RootState) => state.preferencesUser
  );
  const { accountDetails, isAuthenticated } = useSelector(
    (state: RootState) => state.user
  );
  const { isGenerateCodeLoading, generateForDevice, isGenerateCodeError } =
    useSelector((state: RootState) => state.generateCode);
  const [error, setError] = useState<{
    title: string;
    description: string;
  } | null>(null);

  useEffect(() => {
    const response = generateForDevice as GenerateForDevice;

    if (
      (response?.statusCode === '200' || response?.status === '200') &&
      resendClick
    ) {
      setIssuccessMsg(true);
      setRendClick(true);
    } else {
      if (isGenerateCodeError) {
        console.log('It is generate code error');
      }
    }
  }, [generateForDevice, isGenerateCodeError, resendClick]);

  const handelContinue = async () => {
    try {
      const { accountId, customerId } = preferencesUser;
      const token = await getRecaptchaToken('validate_passcode');
      const validationData = {
        accountId: accountId,
        customerId: customerId,
        deviceId: deviceId,
        oneTimePassCode: otp,
        username: userData?.data?.username,
        password: userData?.data?.password,
        channel: getChannelId,
        flow: 'PATH_VALID_CODE_API',
        recaptchaToken: token,
      };
      if (!isDisabled) {
        dispatch(validatePasscodeRequest(validationData));
      }
    } catch (error) {
      console.error('Error during OTP validation:', error);
      // Reset loading states on error
      setIsNavigating(false);
      setIsSuccessLoading(false);
      setError({
        title: 'Validation Error',
        description: 'An error occurred during validation. Please try again.',
      });
    }
  };

  useEffect(() => {
    if (isValidatePasscodeError != null) {
      // Reset navigation state on any error
      setIsNavigating(false);
      setIsSuccessLoading(false);

      const { status, detail, errorCode } = isValidatePasscodeError;

      if (status === '404' && blockAccountText !== detail) {
        setIstryAgain(true);
        setIsValidOtp(false);
        setError(null);
      } else if (status === '404' && blockAccountText === detail) {
        setBlockUser(true);
        setIstryAgain(false);
        setError(null);
      } else {
        if (status == '401') {
          setError({
            title:
              entries?.rb_error?.recaptchaErrors[
                isValidatePasscodeError?.errorCode
              ]?.title,
            description:
              entries?.rb_error?.recaptchaErrors[
                isValidatePasscodeError?.errorCode
              ]?.description,
          });
        } else {
          setError({
            title: entries?.rb_error?.genericApiFailure?.text,
            description: entries?.rb_error?.genericApiFailure?.text,
          });
        }
      }
    }
  }, [isValidatePasscodeError]);

  const onCloseMessage = () => {
    setIssuccessMsg(false);
  };

  useEffect(() => {
    if (validationPasscode) {
      setIsSuccessLoading(true);
      setDisabled(true); // Keep button disabled after successful validation
      setIsNavigating(true); // Start navigation state
      updateAfterValidatePasscode();
    }
  }, [validationPasscode]);

  // Handle navigation after successful authentication
  useEffect(() => {
    if (isNavigating && isAuthenticated && accountDetails) {
      try {
        // Small delay to ensure all data is properly loaded
        const navigationTimeout = setTimeout(() => {
          // The PublicRoute component will automatically handle the navigation to /home
          // We just need to reset our local state
          setIsNavigating(false);
          setIsSuccessLoading(false);
        }, 1000);

        return () => clearTimeout(navigationTimeout);
      } catch (error) {
        console.error('Error during navigation handling:', error);
        setIsNavigating(false);
        setIsSuccessLoading(false);
      }
    }
  }, [isNavigating, isAuthenticated, accountDetails]);

  // Timeout fallback to prevent infinite loading state
  useEffect(() => {
    if (isNavigating) {
      const fallbackTimeout = setTimeout(() => {
        console.warn('Navigation timeout reached, resetting loading state');
        setIsNavigating(false);
        setIsSuccessLoading(false);
        // Don't disable the button here as user might want to retry
      }, 15000); // 15 second fallback

      return () => clearTimeout(fallbackTimeout);
    }
  }, [isNavigating]);

  // Cleanup: Reset validatePasscode state when component unmounts
  useEffect(() => {
    return () => {
      // Reset the validatePasscode state when component unmounts to prevent
      // spinner persistence when signing out and signing in with different account
      dispatch(resetValidatePasscodeSlice());
    };
  }, [dispatch]);

  const updateAfterValidatePasscode = async () => {
    startTimer();
  };
  const startTimer = () => {
    // Set a timer for 3 seconds
    setTimeout(() => {
      setIsSuccessLoading(false);
      //otpVerificationSuccess();
    }, 2000);
  };
  const otpVerificationSuccess = async () => {
    // const deviceId =
    //   '80da27ea267e20a4dd286108bb15bf49%7C0e398ae086b139c47f532b75f3596268';
    const inputData = {
      username: userData?.data?.username?.trim(),
      password: encryptAES(userData?.data?.password?.trim()),
      deviceId: deviceId,
      channel: getChannelId,
      flow: 'PATH_AUTH_API',
    };
    const data = {
      apiName: 'sign_in',
      data: inputData,
    };
    dispatch(signInRequest(data));
  };

  const resendCode = async () => {
    console.log('resendCode', resendClick);
    setRendClick(true);
    setIssuccessMsg(true);
    clearErrors();
    setIstryAgain(false);
    setError(null);
    const { accountId, customerId } = preferencesUser;
    if (isPreferenceType !== null) {
      const token = await getRecaptchaToken('generate_token');
      const generateData = {
        preferenceType: isPreferenceType,
        deviceId: deviceId,
        accountId: accountId,
        customerId: customerId,
        username: userData?.data?.username,
        password: userData?.data?.password,
        channel: getChannelId,
        flow: 'PATH_GEN_CODE_API',
        recaptchaToken: token,
      };
      dispatch(generateCodeForDeviceRequest(generateData));
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isDisabled) {
      e.preventDefault();
      handleSubmit(handelContinue)();
    }
  };
  const handleResendKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      resendCode();
    }
  };
  const handleChangeMethodKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault(); // Prevent scrolling for Space key
      changeToContactmethod();
    }
  };

  const handelClose = () => {
    navigate(0);
  };

  return (
    <>
      {!blockUser && (
        <div
          role="dialog"
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 min-h-screen"
        >
          <div
            className="xl:w-[45%] lg:w-[80%] md:w-[70%] sm:w-full md:max-h-[88vh] md:h-auto max-h-full h-full overflow-y-auto bg-white   md:rounded-[15px] rounded-none shadow-lg  md:p-8 p-[15px]"
            ref={modalRef}
            tabIndex={-1}
          >
            <div className="flex flex-col justify-center items-center relative bg-white">
              <button
                ref={closeButtonRef}
                className="absolute top-0 right-0 text-black hover:text-gray-700 focus:outline-none h-6 w-6"
                aria-label="Close modal"
                onClick={() => navigate(0)}
              >
                <Xclose />
              </button>

              {isSuccessMsg && (
                <div className="mb-5 mt-8">
                  <InlineError
                    errorText={otpContentFullData?.successMsg}
                    onClose={onCloseMessage}
                    fontSize={'15px'}
                    error={false}
                  />
                </div>
              )}
              {(istryAgain || blockUser) && (
                <div className="mb-5 mt-8">
                  <InlineError
                    errorText={`${istryAgain ? otpContentFullData?.invalidCodePopupContent : otpContentFullData?.idAccountLockedErrorContent}`}
                    onClose={onCloseMessage}
                    fontSize={'15px'}
                    error={true}
                  />
                </div>
              )}

              {error && (
                <div className="mb-5 mt-8">
                  <InlineError
                    errorText={error?.description}
                    aria-live="assertive"
                    fontSize={'text-[15px]'}
                    onClose={() => setError(null)}
                    oncloseHide={true}
                  />
                </div>
              )}
              <h2
                id="modal-title"
                className={`xl:text-[28px] lg:text-[16px] xl:font-[400] font-[500] text-left md:text-center sm:mb-5 xl:mb-5 md:mb-0 lg:mb-0 mt-[10px] mb-5 self-baseline md:self-center`}
              >
                {otpContentFullData?.verificationTitle}
              </h2>
              <p
                id="modal-description"
                className="text-[15px] xl:text-[16px] text-left md:text-center font-[400] mb-[30px]"
              >
                {otpContentFullData?.codeSentMsgWeb.replace(
                  '%s',
                  isPreferenceType == 'EMAIL'
                    ? maskEmailID(decryptAES(selectedMethod))
                    : maskPhoneNumber(decryptAES(selectedMethod))
                )}
              </p>

              <form onSubmit={handleSubmit(handelContinue)} noValidate>
                <div className="mb-[30px] flex justify-center items-center flex-col">
                  <label
                    htmlFor="verificationCode"
                    className="block text-[16px] font-[400] w-full md:w-[420px] self-center"
                  >
                    {otpContentFullData?.inputLabel}
                  </label>
                  <div className="relative w-full md:w-[420px]">
                    <input
                      type="text"
                      id="verificationCode"
                      {...register('verificationCode')}
                      maxLength={8}
                      className={`h-[48px] md:max-w-[420px] max-w-[100%] py-[12px] px-[15px] mt-1 block w-full border border-light-gray-border shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm  ${
                        errors.verificationCode ? 'border-red-500' : ''
                      }`}
                      placeholder={otpContentFullData?.idEnterCodeTitle}
                      aria-invalid={errors.verificationCode ? 'true' : 'false'}
                      aria-describedby={
                        errors.verificationCode ? 'error-message' : undefined
                      }
                      onKeyDown={handleInputKeyDown}
                    />

                    {errors.verificationCode && (
                      <div className="flex gap-1 items-center">
                        <div aria-label="balanceIcon">
                          <BalanceIcon fill="#DA291C" width="14" height="14" />
                        </div>
                        <p
                          className="text-red-600"
                          aria-label={errors.verificationCode.message}
                        >
                          {errors.verificationCode.message}
                        </p>
                      </div>
                    )}
                    <button
                      className="absolute top-3 right-2 text-black hover:text-gray-700 focus:outline-none h-6 w-6"
                      aria-label="clear OTP"
                      onClick={() => {
                        resetField('verificationCode');
                        setOtp('');
                      }}
                    >
                      {otp && <Xclose />}
                    </button>
                  </div>
                </div>
                <p
                  className="text-[16px] text-center font-[400] p-6 mb-[30px] bg-[#FAFAFA]"
                  aria-label={
                    otpContentFullData?.blockedUserPopupContent
                      ?.verification_code_content
                  }
                >
                  {
                    otpContentFullData?.blockedUserPopupContent
                      ?.verification_code_content
                  }
                </p>
                <p
                  className="text-[16px] text-center font-[400] mb-[30px]"
                  aria-label={otpContentFullData?.byContinuingAgreeTitle}
                >
                  {otpContentFullData?.byContinuingAgreeTitle}{' '}
                  <a
                    href={errorContent?.otp_terms_condition_url}
                    className="text-primary-red hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={otpContentFullData?.termsConditions}
                  >
                    {otpContentFullData?.termsConditions}
                  </a>{' '}
                  {otpContentFullData?.termsConditionsEndTitle}
                </p>
                <div
                  className="flex justify-center items-center gap-[20px] mt-[30px] border-b border-light-gray-border"
                  style={{ marginBottom: '30px', paddingBottom: '30px' }}
                >
                  <button
                    aria-label={
                      otpContentFullData?.idAccountLockedCtaSecondaryTitle
                    }
                    type="button"
                    onClick={() => navigate(0)}
                    className="p-[12px] text-text16 font-[500] bg-white hover:bg-red-50 text-primary-red focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 border border-primary-red w-[160px] h-[48px]"
                  >
                    {otpContentFullData?.idAccountLockedCtaSecondaryTitle}
                  </button>
                  <div className="relative w-[160px]">
                    <button
                      aria-label={otpContentFullData?.continueButtonTitle}
                      type="submit"
                      // onClick={() => {
                      //   console.log('handel Clicked');
                      //   handelContinue();
                      // }}
                      disabled={
                        isDisabled || isValidatePasscodeLoading || isNavigating
                      }
                      className={`${isDisabled && !isValidatePasscodeLoading && !isNavigating ? 'bg-disabled-bg-color text-Text-disabled-btn border-[#D9D9D9] border min-h-[48px]' : 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500'} min-h-[48px] w-[160px]  font-medium py-2 px-4 focus:outline-none focus:ring-2 focus:ring-opacity-50 h-10"`}
                    >
                      {isValidatePasscodeLoading || isNavigating
                        ? ''
                        : otpContentFullData?.continueButtonTitle}
                    </button>
                    {(isValidatePasscodeLoading || isNavigating) && (
                      <div className="flex justify-center items-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <div className="animate-spin ease-linear rounded-full border-4 border-t-4 border-white h-6 w-6"></div>
                      </div>
                    )}
                  </div>
                </div>
                {/* </div> */}
              </form>
              <p
                className="text-[20px] font-[400] leading-[25px]"
                aria-label={otpContentFullData?.changeContactMethod}
              >
                {otpContentFullData?.changeContactMethod}
              </p>
              <div className="text-center">
                <p
                  className="text-[16px] font-[400] mb-[30px]"
                  aria-label={otpContentFullData?.sendNewCodeTitle}
                >
                  {otpContentFullData?.sendNewCodeTitle}{' '}
                  <span
                    aria-label={otpContentFullData?.resendCodeCtaTitle}
                    tabIndex={0}
                    className="text-primary-red font-bold hover:underline cursor-pointer block md:inline"
                    onClick={() => resendCode()}
                    onKeyDown={handleResendKeyDown}
                  >
                    {otpContentFullData?.resendCodeCtaTitle}
                  </span>
                </p>
                <p
                  aria-label={otpContentFullData?.clickToChangeTitle}
                  className="block mt-2 text-sm text-primary-red hover:underline cursor-pointer pb-5"
                  onClick={changeToContactmethod}
                  onKeyDown={handleChangeMethodKeyDown}
                  tabIndex={0}
                >
                  {otpContentFullData?.clickToChangeTitle}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {blockUser && (
        <CommanModal
          jsxContent={
            <div className="flex flex-col items-center justify-center">
              <h1
                className="font-[650] text-[22px] text-center mb-[12px]"
                aria-label={errorContent?.modalTitle}
              >
                {errorContent?.modalTitle}
              </h1>
              <p
                className="font-[400] text-[16px] text-center"
                aria-label={errorContent?.modalDesc}
              >
                {errorContent?.modalDesc}
              </p>
            </div>
          }
          isOpen={blockUser}
          buttonText={errorContent?.modalOkCtaBtn}
          onClose={handelClose}
          closeIcon={false}
        />
      )}
    </>
  );
};

export default VerificationCodeModal;
